import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Input, Table } from '../components/ui';
import { ConfirmDialog, EmptyStateBlock, ErrorState, LoadingState } from '../components/states';
import { useToast } from '../app/ToastProvider';
import { useSession } from '../app/session';
import { ApiError } from '../api/client';
import { createApiKey, listApiKeys, listDataKeys, revokeApiKey, type ApiKeyRow, type CreatedApiKey, type DataKeyRow } from '../api/dataSources';

/** A compact, human value for the table (objects → JSON, long strings clipped). */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') {
    const json = JSON.stringify(value);
    return json.length > 80 ? `${json.slice(0, 77)}…` : json;
  }
  return String(value);
}

function formatWhen(iso: string | null): string {
  if (!iso) return 'never';
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleString();
}

/** The scope list a key carries (empty scopes = any data key). */
function scopeLabel(scopes: unknown): string {
  const keys = (scopes as { keys?: unknown } | null)?.keys;
  return Array.isArray(keys) && keys.length > 0 ? keys.join(', ') : 'any data key';
}

/** A copyable ingest curl. `key` may be a placeholder when the plaintext key is gone. */
function ingestCurl(key: string, dataKey: string): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  return `curl -X POST '${origin}/api/v1/ingest/${key}' \\\n  -H 'content-type: application/json' \\\n  -d '{"key":"${dataKey}","value":42}'`;
}

/**
 * A5b.5 — Data sources & API keys.
 *
 * The tenant's live `data_points` and the ingest API keys that write them. The
 * plaintext key is shown **once** on creation (only its hash is stored) with a
 * copyable ingest curl; revoking a key makes ingest with it fail immediately.
 * The admin cannot read PocketBase directly, so every read here goes through the
 * Node gateway.
 */
export function DataSources() {
  const { session } = useSession();
  const { notify, notifyError } = useToast();
  const writable = (session?.role ?? 'viewer') !== 'viewer';

  const [apiKeys, setApiKeys] = useState<ApiKeyRow[]>([]);
  const [dataKeys, setDataKeys] = useState<DataKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [keysCsv, setKeysCsv] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [copied, setCopied] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyRow | null>(null);

  const load = useCallback(async () => {
    try {
      const [keys, data] = await Promise.all([listApiKeys(), listDataKeys()]);
      setApiKeys(keys);
      setDataKeys(data);
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load data sources.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function copy(text: string, tag: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tag);
      notify('Copied to the clipboard');
    } catch {
      notify('Clipboard unavailable — copy from the field');
    }
  }

  async function create() {
    if (!name.trim()) return;
    const keys = keysCsv.split(',').map((entry) => entry.trim()).filter(Boolean);
    setCreating(true);
    try {
      const key = await createApiKey({ name: name.trim(), ...(keys.length > 0 ? { keys } : {}) });
      // The plaintext key exists only in this response — keep it on screen until
      // the operator has copied it, then it is gone forever.
      setCreated(key);
      setName('');
      setKeysCsv('');
      notify('API key created — copy it now, it is shown once');
      await load();
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not create the key.');
    } finally {
      setCreating(false);
    }
  }

  async function revoke() {
    if (!revokeTarget) return;
    try {
      await revokeApiKey(revokeTarget.id);
      notify(`Revoked ${revokeTarget.name}`);
      if (created?.api_key_id === revokeTarget.id) setCreated(null);
      setRevokeTarget(null);
      await load();
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not revoke the key.');
    }
  }

  return (
    <main className="workspace-main">
      <header className="page-header">
        <div>
          <span className="index-label">CONTROL ROOM / DATA SOURCES</span>
          <h1>Data sources &amp; API keys</h1>
        </div>
      </header>

      <p className="detail-note">
        Ingest keys write live values to a dashboard tile or a text binding. The plaintext key is
        shown once on creation and never stored — the server keeps only its hash.
      </p>

      {loading && <LoadingState label="Loading data sources" />}
      {error && <ErrorState title="Data sources unavailable" detail={error} onRetry={() => void load()} />}

      {!loading && !error && (
        <section className="panel">
          <header className="panel-heading">
            <span className="index-label">API KEYS / {String(apiKeys.length).padStart(2, '0')}</span>
          </header>

          <div className="panel-body">
            <label className="index-label" htmlFor="key-name">NAME</label>
            <Input
              id="key-name"
              value={name}
              placeholder="e.g. Visitor counter feed"
              disabled={!writable || creating}
              onChange={(event) => setName(event.target.value)}
            />
            <label className="index-label" htmlFor="key-scopes">SCOPED DATA KEYS (COMMA-SEPARATED; EMPTY = ANY)</label>
            <Input
              id="key-scopes"
              value={keysCsv}
              placeholder="e.g. visitors, temperature"
              disabled={!writable || creating}
              onChange={(event) => setKeysCsv(event.target.value)}
            />
            <div className="settings-actions">
              <Button type="button" disabled={!writable || creating || !name.trim()} onClick={() => void create()}>
                {creating ? 'Creating…' : 'Create key'}
              </Button>
            </div>

            {created && (
              <div className="data-source-secret" data-created-key>
                <span className="index-label">NEW KEY — SHOWN ONCE</span>
                <pre className="data-source-secret-value mono" data-created-key-value>{created.key}</pre>
                <div className="settings-actions">
                  <Button variant="quiet" type="button" onClick={() => void copy(created.key, 'key')}>
                    {copied === 'key' ? 'Copied' : 'Copy key'}
                  </Button>
                  <Button variant="quiet" type="button" onClick={() => void copy(ingestCurl(created.key, 'visitors'), 'curl')}>
                    {copied === 'curl' ? 'Copied' : 'Copy ingest curl'}
                  </Button>
                </div>
                <p className="detail-note">Store it now — it cannot be shown again. Revoke it to disable ingest immediately.</p>
              </div>
            )}
          </div>

          {apiKeys.length === 0 ? (
            <div className="panel-body">
              <EmptyStateBlock title="No API keys" detail="Create a key above to start ingesting live values." />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Scopes</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td>{scopeLabel(row.scopes)}</td>
                    <td>{formatWhen(row.created)}</td>
                    <td>
                      <Button variant="quiet" type="button" disabled={!writable} onClick={() => setRevokeTarget(row)}>
                        Revoke
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </section>
      )}

      {!loading && !error && (
        <section className="panel">
          <header className="panel-heading">
            <span className="index-label">LIVE DATA KEYS / {String(dataKeys.length).padStart(2, '0')}</span>
          </header>
          {dataKeys.length === 0 ? (
            <div className="panel-body">
              <EmptyStateBlock
                title="No data yet"
                detail="Ingest a value with an API key (see the curl above) and it appears here."
              />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Value</th>
                  <th>Version</th>
                  <th>Last seen</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {dataKeys.map((row) => (
                  <tr key={row.key}>
                    <td className="mono">{row.key}</td>
                    <td className="mono">{formatValue(row.value)}</td>
                    <td>{row.version}</td>
                    <td>{formatWhen(row.last_seen)}</td>
                    <td>
                      <Button variant="quiet" type="button" onClick={() => void copy(ingestCurl('<API_KEY>', row.key), `curl:${row.key}`)}>
                        {copied === `curl:${row.key}` ? 'Copied' : 'Copy curl'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </section>
      )}

      <ConfirmDialog
        open={revokeTarget !== null}
        title="Revoke API key"
        confirmWord="REVOKE"
        detail={`Ingest with “${revokeTarget?.name ?? ''}” will be rejected immediately. This cannot be undone.`}
        confirmLabel="Revoke"
        onConfirm={() => void revoke()}
        onClose={() => setRevokeTarget(null)}
      />
    </main>
  );
}
